<template>
    <div class="flex flex-col gap-4">
        <h2 class="body-small-bold text-ink">Onboarding tools</h2>
        <div class="flex flex-col gap-2">
            <BaseButton theme="neutral" size="sm" class="tool-row" :disabled="busy" @click="startContextOnboarding">
                <i class="bi bi-play-circle shrink-0" aria-hidden="true"></i>
                <span class="grow truncate text-left">Start context onboarding</span>
                <i v-if="busy" class="bi bi-arrow-repeat shrink-0 spin" aria-hidden="true"></i>
            </BaseButton>
            <p class="details text-ink-muted">
                Takes you to the first screen for your account. Turns on the flag and its three prerequisites, clears your onboarding state, and disconnects your calendar so the
                connect step is a real ask again.
            </p>
        </div>

        <div class="fill-section">
            <h3 class="body-small-bold text-ink">Coaching setup (private profile)</h3>

            <BaseButton theme="neutral" size="sm" class="tool-row" :disabled="busy" @click="resetSetup([], { publicProfile: true })">
                <i class="bi bi-arrow-counterclockwise shrink-0" aria-hidden="true"></i>
                <span class="grow truncate text-left">Reset all four asks</span>
                <i v-if="busy" class="bi bi-arrow-repeat shrink-0 spin" aria-hidden="true"></i>
            </BaseButton>

            <BaseButton
                theme="neutral"
                size="sm"
                class="tool-row"
                :disabled="busy"
                aria-label="Reset the setup as an org without collaboration profiles, leaving three asks"
                @click="resetSetup([], { publicProfile: false })">
                <i class="bi bi-three-dots shrink-0" aria-hidden="true"></i>
                <span class="grow truncate text-left">Reset as a three-ask org</span>
                <i v-if="busy" class="bi bi-arrow-repeat shrink-0 spin" aria-hidden="true"></i>
            </BaseButton>

            <BaseButton
                v-for="ask in SETUP_ASKS"
                :key="ask.id"
                theme="neutral"
                size="sm"
                class="tool-row"
                :disabled="busy"
                :aria-label="`Clear ${ask.label}, so that ask is outstanding again`"
                @click="resetSetup([ask.id])">
                <i class="bi bi-eraser shrink-0" aria-hidden="true"></i>
                <span class="grow truncate text-left">Clear {{ ask.label }}</span>
            </BaseButton>

            <p class="details text-ink-muted">
                Deletes the real data each ask produces from your account, so it reads as outstanding again, and rebuilds the run. Nothing is seeded — an ask goes back to done by
                doing it, which is the only way what you then review is the real thing. The three-ask reset turns public_profile off, dropping the collaboration profile ask the way
                it drops for an org that never bought it; clearing a single ask leaves that choice alone.
            </p>
        </div>

        <!-- Only while the Perspectives step is the one on screen: elsewhere
         there is no form for it to act on. -->
        <div v-if="onboardingDevToolsStore.canFillAssessment" class="fill-section">
            <h3 class="body-small-bold text-ink">On this step</h3>
            <BaseButton theme="neutral" size="sm" class="tool-row" aria-label="Fill every unanswered Perspectives question" @click="fillAssessment">
                <i class="bi bi-shuffle shrink-0" aria-hidden="true"></i>
                <span class="grow truncate text-left">Fill remaining answers</span>
                <span class="seed-tag shrink-0" title="Fills every unanswered question server-side and skips to the last page">synthetic</span>
            </BaseButton>
        </div>
        <p v-if="inlineError" class="details text-status-error" role="alert">{{ inlineError }}</p>
    </div>
</template>

<script setup lang="ts">
import { useFetch } from "~vue/api";
import BaseButton from "~vue/components/BaseButton.vue";
import type { SetupStepId } from "~vue/ProfileSetup/types";
import { onboardingDevToolsStore, requestAssessmentFill } from "~vue/stores/onboardingDevToolsStore";
import { ref } from "vue";

const emit = defineEmits<{ close: [] }>();
const busy = ref(false);
const inlineError = ref<string | null>(null);

// `immediate: false` means the request only fires on `execute()`; awaiting the
// chain alone resolves without ever hitting the network.
const {
    execute: executeStart,
    data: startData,
    error: startError,
    statusCode,
} = useFetch("/onboarding/calendar-first/dev/start/", { immediate: false }).post({}).json<{ redirect_url: string; notes: string[] }>();

const SETUP_ASKS: { id: SetupStepId; label: string }[] = [
    { id: "identity", label: "name and role" },
    { id: "calendar", label: "calendar" },
    { id: "perspective", label: "perspective assessment" },
    { id: "publish", label: "collaboration profile" },
];

/** `public_profile` omitted leaves the flag where it is -- see `ResetSetupRequest`. */
const resetBody = ref<{ steps: SetupStepId[]; public_profile?: boolean }>({ steps: [] });
const {
    execute: executeReset,
    error: resetError,
    statusCode: resetStatus,
} = useFetch("/onboarding/calendar-first/dev/setup/reset/", { immediate: false }).post(resetBody).json<{ cleared: string[]; notes: string[] }>();

function fillAssessment(): void {
    requestAssessmentFill();
    emit("close");
}

/**
 * An empty selection resets every ask the user is offered. Reload to refresh Inertia setup props and discard
 * any panel displaying an ask invalidated by the reset.
 */
async function resetSetup(steps: SetupStepId[], { publicProfile }: { publicProfile?: boolean } = {}): Promise<void> {
    if (busy.value) {
        return;
    }
    busy.value = true;
    inlineError.value = null;
    resetBody.value = publicProfile === undefined ? { steps } : { steps, public_profile: publicProfile };

    await executeReset();
    busy.value = false;

    if (resetError.value) {
        inlineError.value =
            resetStatus.value === 403 || resetStatus.value === 404
                ? "These tools are staff-only and unavailable for this account."
                : resetError.value.message || "Could not reset the coaching setup.";
        return;
    }

    globalThis.location.reload();
}

async function startContextOnboarding(): Promise<void> {
    if (busy.value) {
        return;
    }
    busy.value = true;
    inlineError.value = null;

    await executeStart();
    busy.value = false;

    const redirectUrl = startData.value?.redirect_url;
    if (startError.value || !redirectUrl) {
        inlineError.value =
            statusCode.value === 403 || statusCode.value === 404
                ? "These tools are staff-only and unavailable for this account."
                : startError.value?.message || "Could not set up the flow.";
        return;
    }

    globalThis.location.href = redirectUrl;
}
</script>

<style scoped>
@reference "../../../css/tailwind.css";

@layer components {
    .tool-row {
        @apply flex w-full items-center gap-2 overflow-hidden text-left;
    }

    .fill-section {
        @apply flex flex-col gap-2 border-t border-border pt-3;
    }

    .seed-tag {
        @apply details rounded-full bg-surface-sunken px-2 py-1 text-ink-muted;
    }

    .spin {
        animation: dev-tools-spin 1s linear infinite;
    }
}

@keyframes dev-tools-spin {
    to {
        transform: rotate(360deg);
    }
}

@media (prefers-reduced-motion: reduce) {
    .spin {
        animation: none;
    }
}
</style>
